import global from "@/global";
import { createResourceService } from "@/query//createResourceService";
import { Patient } from "@/resources/types";
import { WORK_OFFLINE } from "@resources/constants";
import { createMockPatients } from "@pages/home/patient-list/mocking/patient-list-mocking";

const { dataProvider, queryClient, client } = global;

// Create the base service
const baseService = createResourceService<Patient>({
  resource: "patients",
  client,
  dataProvider,
  queryClient,
});

// Mock implementation of useGetList for patients
const useMockPatientList = (options: any = {}) => {
  const mockPatients = createMockPatients(50); // Create 50 mock patients

  // Filter patients based on search term if provided
  let filteredPatients = mockPatients;
  if (options.meta?.search) {
    const searchTerm = options.meta.search.replace(/\*/g, "").toLowerCase();
    filteredPatients = mockPatients.filter((patient: Patient) =>
      patient.firstName?.toLowerCase().includes(searchTerm) ||
      patient.lastName?.toLowerCase().includes(searchTerm) ||
      patient.umrn?.toLowerCase().includes(searchTerm)
    );
  }

  // Sort patients if sort is provided - simplified to avoid complexity
  if (options.meta?.sort) {
    const { field, order } = options.meta.sort;
    filteredPatients.sort((a: Patient, b: Patient) => {
      // For now, just sort by name since Patient type doesn't have lastModified
      const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
      const bName = `${b.firstName} ${b.lastName}`.toLowerCase();

      if (aName < bName) return order === "asc" ? -1 : 1;
      if (aName > bName) return order === "asc" ? 1 : -1;
      return 0;
    });
  }

  return {
    data: { data: filteredPatients, total: filteredPatients.length },
    isLoading: false,
    isFetching: false,
    isFetched: true,
    refetch: () => Promise.resolve(),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    remove: () => {}, // Stub for clearing cache
  };
};

// Export the service with mocked or real implementation
export const patientService = WORK_OFFLINE ? {
  ...baseService,
  useGetList: useMockPatientList,
  useGetOne: () => ({
    data: createMockPatients(1)[0],
    isLoading: false,
    isFetching: false,
    refetch: () => Promise.resolve(),
  }),
  useCreate: () => ({
    mutateAsync: async () => createMockPatients(1)[0],
    isLoading: false,
    isFetching: false,
  }),
  useUpdate: () => ({
    mutateAsync: async () => createMockPatients(1)[0],
    isLoading: false,
    isFetching: false,
  }),
  useDelete: () => ({
    mutateAsync: async () => createMockPatients(1)[0],
    isLoading: false,
    isFetching: false,
  }),
} : baseService;